import {
  HsCullingPlane,
  hsMarkSphereCullingTree,
  HsSphereCullingTree,
  HsSphereCullingWorkerResponse,
} from '../../../shared/culling/sphere-culling-tree.function';
import { hsCollectSparseCullingCandidates, HsSparseCullingCandidates } from './sparse-culling-candidates.function';

export interface HsCullingObject {
  name?: string;
  boundingSphere: { center: { x: number; y: number; z: number }; radius: number };
}

export interface HsCullingWorker {
  onmessage: ((event: MessageEvent<HsSphereCullingWorkerResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
  postMessage: (message: Float64Array, transfer: Transferable[]) => void;
  terminate: () => void;
}

interface CullingBuild<T> {
  objects: readonly T[];
  worker: HsCullingWorker | null;
  timer: ReturnType<typeof setTimeout> | null;
  fallbackCount: number;
}

const MIN_INDEX_OBJECTS: number = 2000;
const SNAPSHOT_CHUNK: number = 256;
const SNAPSHOT_BUDGET_MS: number = 4;

export class HsSpatialCulling<T extends HsCullingObject> {
  private objects: readonly T[] | null = null;
  private tree: HsSphereCullingTree | null = null;
  private marked: Uint8Array = new Uint8Array();
  private fallback: Uint8Array = new Uint8Array();
  private build: CullingBuild<T> | null = null;
  private disabled: boolean = false;
  private candidates: HsSparseCullingCandidates | null = null;
  private fallbackCount: number = 0;
  private candidateCount: number = 0;
  private indexedObjects: WeakMap<T, number> = new WeakMap();
  private indexedMarked: Uint8Array = new Uint8Array();
  private queryObjects: readonly T[] | null = null;
  private queryIndices: Int32Array = new Int32Array();
  private needsReconcile: boolean = false;
  private changedCount: number = 0;

  constructor(
    private readonly createWorker: () => HsCullingWorker,
    private readonly canBuild: () => boolean,
    private readonly reportError: (message: string) => void
  ) {}

  public invalidate(): void {
    if (this.objects === null && this.tree === null && this.build === null) {
      return;
    }
    this.objects = null;
    this.tree = null;
    this.marked = new Uint8Array();
    this.fallback = new Uint8Array();
    this.candidates = null;
    this.fallbackCount = 0;
    this.candidateCount = 0;
    this.indexedObjects = new WeakMap();
    this.indexedMarked = new Uint8Array();
    this.queryObjects = null;
    this.queryIndices = new Int32Array();
    this.needsReconcile = false;
    this.changedCount = 0;
    this.cancelBuild();
  }

  public refresh(): void {
    this.needsReconcile = true;
    this.cancelBuild();
  }

  public destroy(): void {
    this.invalidate();
    this.disabled = true;
  }

  public query(objects: readonly T[], planes: readonly HsCullingPlane[]): Uint8Array | null {
    if (this.tree !== null && objects.length < MIN_INDEX_OBJECTS) {
      this.invalidate();
    }
    if (this.build !== null && this.build.objects !== objects) {
      this.cancelBuild();
    }
    if (this.tree !== null && this.objects !== null) {
      if (this.queryObjects !== objects || this.needsReconcile) {
        this.reconcile(objects);
      }
      this.scheduleBuild(objects, this.changedCount > Math.max(256, this.objects.length / 4));
      const count: number =
        hsMarkSphereCullingTree(this.tree, planes, this.indexedMarked, this.fallback) + this.fallbackCount;
      if (this.queryObjects === this.objects && this.changedCount === 0) {
        this.marked = this.indexedMarked;
        this.candidateCount = count;
      } else {
        if (this.marked === this.indexedMarked || this.marked.length !== objects.length) {
          this.marked = new Uint8Array(objects.length);
        }
        this.candidateCount = 0;
        for (let index: number = 0; index < objects.length; index++) {
          const indexed: number = this.queryIndices[index];
          const value: number = indexed < 0 ? 2 : this.indexedMarked[indexed];
          this.marked[index] = value;
          this.candidateCount += value === 0 ? 0 : 1;
        }
      }
      return this.marked;
    }
    this.scheduleBuild(objects, true);
    return null;
  }

  public selectCandidates(objects: readonly T[], mask: Uint8Array): HsSparseCullingCandidates | null {
    if (
      this.queryObjects !== objects ||
      this.marked !== mask ||
      this.candidates === null ||
      this.candidateCount > this.candidates.indices.length
    ) {
      return null;
    }
    const count: number | null = hsCollectSparseCullingCandidates(objects, mask, this.candidates.indices);
    if (count === null) {
      return null;
    }
    this.candidates.count = count;
    return this.candidates;
  }

  private cancelBuild(): void {
    if (this.build !== null) {
      if (this.build.timer !== null) {
        clearTimeout(this.build.timer);
      }
      this.build.worker?.terminate();
      this.build = null;
    }
  }

  private scheduleBuild(objects: readonly T[], needed: boolean): void {
    if (needed && !this.disabled && this.build === null && objects.length >= MIN_INDEX_OBJECTS && this.canBuild()) {
      const build: CullingBuild<T> = { objects, worker: null, timer: null, fallbackCount: 0 };
      this.build = build;
      build.timer = setTimeout(() => this.snapshot(build), 150);
    }
  }

  private reconcile(objects: readonly T[]): void {
    if (this.tree === null || this.objects === null) {
      return;
    }
    if (this.queryIndices.length !== objects.length) {
      this.queryIndices = new Int32Array(objects.length);
      this.candidates = { indices: new Uint32Array(Math.floor(objects.length / 4)), count: 0 };
    }
    let retained: number = 0;
    for (let index: number = 0; index < objects.length; index++) {
      const object: T = objects[index];
      const indexed: number | undefined = this.indexedObjects.get(object);
      const { center, radius } = object.boundingSphere;
      const offset: number = (indexed ?? -1) * 4;
      const unchanged: boolean =
        indexed !== undefined &&
        Object.is(center.x, this.tree.spheres[offset]) &&
        Object.is(center.y, this.tree.spheres[offset + 1]) &&
        Object.is(center.z, this.tree.spheres[offset + 2]) &&
        Object.is(radius, this.tree.spheres[offset + 3]);
      this.queryIndices[index] = unchanged && indexed !== undefined ? indexed : -1;
      retained += unchanged ? 1 : 0;
    }
    this.changedCount = Math.max(objects.length, this.objects.length) - retained;
    this.queryObjects = objects;
    this.needsReconcile = false;
  }

  private snapshot(build: CullingBuild<T>): void {
    if (this.build !== build || !this.canBuild()) {
      if (this.build === build) {
        this.build = null;
      }
      return;
    }
    const spheres: Float64Array = new Float64Array(build.objects.length * 4);
    const fallback: Uint8Array = new Uint8Array(build.objects.length);
    let index: number = 0;
    const advance = (): void => {
      if (this.build !== build) {
        return;
      }
      const started: number = performance.now();
      while (index < build.objects.length) {
        const end: number = Math.min(index + SNAPSHOT_CHUNK, build.objects.length);
        for (; index < end; index++) {
          const { center, radius } = build.objects[index].boundingSphere;
          const offset: number = index * 4;
          spheres[offset] = center.x;
          spheres[offset + 1] = center.y;
          spheres[offset + 2] = center.z;
          spheres[offset + 3] = radius;
          if (
            !Number.isFinite(center.x) ||
            !Number.isFinite(center.y) ||
            !Number.isFinite(center.z) ||
            !Number.isFinite(radius) ||
            radius < 0
          ) {
            fallback[index] = 2;
            build.fallbackCount++;
          }
        }
        if (performance.now() - started >= SNAPSHOT_BUDGET_MS && index < build.objects.length) {
          build.timer = setTimeout(advance, 0);
          return;
        }
      }
      this.startWorker(build, spheres, fallback);
    };
    advance();
  }

  private startWorker(build: CullingBuild<T>, spheres: Float64Array, fallback: Uint8Array): void {
    try {
      const worker: HsCullingWorker = this.createWorker();
      build.worker = worker;
      worker.onmessage = (event: MessageEvent<HsSphereCullingWorkerResponse>) => {
        if (this.build !== build) {
          return;
        }
        if (!event.data.ok) {
          this.fail(event.data.error);
          return;
        }
        this.tree = event.data.tree;
        this.objects = build.objects;
        this.indexedObjects = new WeakMap();
        for (let index: number = 0; index < build.objects.length; index++) {
          this.indexedObjects.set(build.objects[index], index);
        }
        this.fallback = fallback;
        this.fallbackCount = build.fallbackCount;
        this.indexedMarked = new Uint8Array(build.objects.length);
        this.marked = this.indexedMarked;
        this.queryObjects = null;
        this.needsReconcile = true;
        this.candidates = { indices: new Uint32Array(Math.floor(build.objects.length / 4)), count: 0 };
        if (build.timer !== null) {
          clearTimeout(build.timer);
        }
        worker.terminate();
        this.build = null;
      };
      worker.onerror = (event: ErrorEvent) => {
        if (this.build === build) {
          this.fail(event.message !== '' ? event.message : 'Culling worker failed');
        }
      };
      worker.onmessageerror = () => {
        if (this.build === build) {
          this.fail('Culling worker response could not be decoded');
        }
      };
      build.timer = setTimeout(() => {
        if (this.build === build) {
          this.fail('Culling worker build timed out');
        }
      }, 15000);
      worker.postMessage(spheres, [spheres.buffer]);
    } catch (error: unknown) {
      this.fail(error instanceof Error ? error.message : 'Culling worker could not start');
    }
  }

  private fail(message: string): void {
    this.invalidate();
    this.disabled = true;
    this.reportError(`${message}. Using ordinary frustum culling.`);
  }
}
